import React, { CSSProperties, useState } from "react";
import Image from "next/image";
import useSlider from "@hooks/useSlider";
import useIsMobile from "@hooks/useIsMobile";

interface AIChatbotSectionProps {
    sections: Section[];
    isArabic?: boolean;
    titleStyleProps?: CSSProperties;
    version?: "v2";
}
interface Section {
    title: string;
    icon: string;
    title2: string;
    desc: string;
    img: string;
}

interface CatalogChatbotSectionSliderProps {
    data: Section[];
    renderElem: (section: Section, index: number) => React.ReactNode;
}

const CatalogChatbotSectionSlider: React.FC<CatalogChatbotSectionSliderProps> = ({ data, renderElem }) => {
    const { refCallback, loaded, slider, currentSlide } = useSlider();

    return (
        <div
            className="app-slider"
            style={
                {
                    "--dot-bg": "#D0D5DD",
                    "--dot-active-bg": "#2E90FA",
                } as React.CSSProperties
            }
        >
            <div>
                <div ref={refCallback} className="keen-slider">
                    {data.map((slide: any, idx: number) => {
                        return (
                            <div className="keen-slider__slide" key={`catalog-slide-${idx}`}>
                                {renderElem(slide, idx)}
                            </div>
                        );
                    })}
                </div>
            </div>
            {loaded && slider.current && (
                <div className="dots">
                    {[...Array(slider.current.track.details.slides.length).keys()].map((idx) => {
                        return (
                            <button
                                key={idx}
                                onClick={() => {
                                    slider.current?.moveToIdx(idx);
                                }}
                                className={"dot" + (currentSlide === idx ? " active" : "")}
                            ></button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

interface CatalogChatbotSectionContentProps {
    title: string;
    desc: string;
    img: string;
    isArabic?: boolean;
    currIndex: number;
}
const CatalogChatbotSectionContent: React.FC<CatalogChatbotSectionContentProps> = ({ title, desc, img, isArabic, currIndex }) => (
    <div className="flex flex-col-reverse md:flex-row md:bg-lightBlue rounded-2xl p-0 md:p-10 rounded-xl">
        <div className="md:w-1/2 flex flex-col justify-center">
            <div className={`mb-8 ${isArabic ? "text-right" : "text-left"}`}>
                <h3 className="font-bold text-4xl">{title}</h3>
                <p className="text-lg pt-4">{desc}</p>
            </div>
        </div>

        <div className="md:w-1/2 flex justify-center items-center">
            <div className="fi max-w-full max-h-full p-10 image-container transition-opacity duration-100 loaded">
                <Image width={521} height={597} src={img} alt={`Image ${currIndex}`} priority />
            </div>
        </div>
    </div>
);

const CatalogChatbotSection: React.FC<AIChatbotSectionProps> = ({ sections, isArabic, titleStyleProps, version }) => {
    const isMobile = useIsMobile(768);
    const [currIndex, setCurrIndex] = useState<number>(0);

    const handleBtnSelect = (index: number) => {
        setCurrIndex(index);
    };

    if (isMobile && isArabic) return null;

    const v2ContainerStyle = {
        "--max-width": "1280px",
    };

    return (
        <section className="payments-spl-cont mt-20 mb-4 pb-8 md:mt-32" style={{ ...((version === "v2" ? v2ContainerStyle : {}) as React.CSSProperties) }}>
            {isArabic ? (
                <h3 className="pb-8 text-4xl md:text-6xl font-bold text-center hidden md:block">تحويل محادثات الواتس اب الخاصة بك إلى إيرادات</h3>
            ) : (
                <h3 className="md:text-6xl text-4xl font-semibold pb-10 max-w-7xl mx-auto px-5 font-degular text-center" style={{ ...titleStyleProps }}>
                    Turn your WhatsApp conversations into revenue
                </h3>
            )}

            {!isMobile && (
                <div className="button-cont-cat flex-row pb-0">
                    {sections.map((section, index) => (
                        <button
                            key={section.title}
                            onClick={() => handleBtnSelect(index)}
                            className={`${currIndex === index ? "selected-button-cat" : "button-cont-cat-inactive"}`}
                        >
                            <div className="flex row justify-center">
                                <Image width={16} height={16} src={section.icon} alt={`Icon ${index}`} className="button-icon w-5 h-5" />
                                <p style={{ marginLeft: "10px" }}>{section.title}</p>
                            </div>
                        </button>
                    ))}
                </div>
            )}

            {!isMobile && (
                <div className="hidden md:block">
                    <CatalogChatbotSectionContent
                        title={sections[currIndex]?.title2}
                        desc={sections[currIndex]?.desc}
                        img={sections[currIndex]?.img}
                        currIndex={currIndex}
                        isArabic={isArabic}
                    />
                </div>
            )}

            {isMobile && !isArabic && (
                <div className="block md:hidden bg-blue-50 mx-5 rounded-xl p-5">
                    <CatalogChatbotSectionSlider
                        data={sections}
                        renderElem={({ title2, desc, img }, index) => <CatalogChatbotSectionContent title={title2} desc={desc} img={img} currIndex={index} isArabic={isArabic} />}
                    />
                </div>
            )}
        </section>
    );
};

export default CatalogChatbotSection;
